import { ChartEngine } from '../core/ChartEngine';
import { AddRegionCommand, RemoveRegionCommand } from '../commands/RegionCmd';
import { AddLayerCommand, RemoveLayerCommand } from '../commands/LayerCmd';
import { AddGraphCommand } from '../commands/AddGraph';
import { AddObjectCommand, RemoveObjectCommand } from '../commands/ObjectCmd';
import { AddMaskCommand, RemoveMaskCommand } from '../commands/MaskCmd';
import { AddNextBtnBoxCommand, RemoveNextBtnBoxCommand } from '../commands/NextBtnBoxCmd';

export interface Flow {
  step: number;
  action: any | any[];
}

export class FlowRunner {
  constructor(private engine: ChartEngine) {}

  private normalizeStyle(style?: { [key: string]: any }) {
    if (!style) return {};
    const normalized: { [key: string]: any } = {};
    Object.entries(style).forEach(([key, value]) => {
      const kebabKey = key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
      normalized[kebabKey] = value;
    });
    return normalized;
  }

  executeCommand(action: any) {
    if (!action || !action.type || !action.payload) {
      console.warn(`[FlowRunner] 유효하지 않은 액션 형식입니다.`, action);
      return;
    }

    const { type, payload } = action;

    switch (type) {
      //0.초기화
      case 'RESET_ALL':
        this.engine.resetAll();
        break;
      // 1. 영역 (Region) 관련
      case 'ADD_REGION':
        this.engine.runCommand(new AddRegionCommand({
          id: payload.id,
          name: payload.name ?? '',
          width: payload.width ?? '100%',
          height: payload.height ?? '100%',
          top: payload.top ?? 0,
          left: payload.left ?? 0,
          style: this.normalizeStyle(payload.style)
        }));
        break;

      case 'REMOVE_REGION':
        // payload.regionId와 payload.id를 모두 체크하여 유연하게 대응
        const targetRegionId = payload.regionId || payload.id;
        if (targetRegionId) {
          this.engine.runCommand(new RemoveRegionCommand(targetRegionId));
        }
        break;

      // 2. 레이어 (Layer) 관련
      case 'ADD_LAYER': {
        const { regionId, layer } = payload;
        if (!layer) break;
        this.engine.runCommand(new AddLayerCommand(regionId, {
          id: layer.id,
          width: payload.width ?? layer.width ?? '100%',
          height: payload.height ?? layer.height ?? '100%',
          top: payload.top ?? layer.top ?? '0px',
          left: payload.left ?? layer.left ?? '0px',
          zIndex: layer.zIndex ?? 0,
          style: this.normalizeStyle(payload.style ?? layer.style),
          axes: {
            yAxis: {
              orient: layer.yAxis?.orient ?? 'right',
              ticks: layer.yAxis?.ticks,
              showGrid: layer.yAxis?.showGrid,
              yMinAxisSource: layer.yAxis?.yMinAxisSource,
              yMaxAxisSource: layer.yAxis?.yMaxAxisSource,
            },
            xAxis: layer.xAxis
          }
        } as any));
        break;
      }

      case 'REMOVE_LAYER':
        this.engine.runCommand(new RemoveLayerCommand(payload.regionId, payload.layerId));
        break;

      // 3. 그래프 (Graph) 관련
      case 'ADD_GRAPH': {
        const { regionId, layerId, graph } = payload;
        if (!graph) break;
        this.engine.runCommand(new AddGraphCommand(regionId, layerId, {
          id: graph.id,
          type: graph.type,
          usedFields: graph.usedFields ?? [],
          mapping: graph.mapping,
          style: graph.style ?? {},
          transform: graph.transform,
        }));
        break;
      }

      // 4. 일반 도형 (Object) 관련
      case 'ADD_OBJECT': {
        const { regionId, layerId, object } = payload;
        if (!object?.id) break;
        this.engine.runCommand(new AddObjectCommand(regionId, layerId, {
          ...object,
          style: this.normalizeStyle(object.style)
        }));
        break;
      }

      case 'REMOVE_OBJECT':
        this.engine.runCommand(new RemoveObjectCommand(payload.regionId, payload.layerId, payload.objectId));
        break;

      // 5. 마스크 (Mask) 관련
      case 'ADD_MASK': {
        const { regionId, layerId, mask } = payload;
        if (!mask?.id) break;
        this.engine.runCommand(new AddMaskCommand(regionId, layerId, {
          id: mask.id,
          type: mask.type,
          x: mask.x,
          y: mask.y,
          width: mask.width,
          height: mask.height,
          radius: mask.radius,
          borderRadius: mask.borderRadius,
          points: mask.points
        }));
        break;
      }

      case 'REMOVE_MASK':
        this.engine.runCommand(new RemoveMaskCommand(payload.regionId, payload.layerId, payload.maskId));
        break;

      // 6. 안내 박스 (NextBtnBox) 관련
      case 'ADD_NEXT_BTN_BOX': {
        const { regionId, layerId, box, x, y } = payload;
        if (!box?.id) break;
        this.engine.runCommand(new AddNextBtnBoxCommand(regionId, layerId, {
          id: box.id,
          title: box.title ?? '',
          content: box.content ?? '',
          x: x ?? box.x ?? '50%',
          y: y ?? box.y ?? '50%',
          nextStep: box.nextStep,
          btnContent: box.btnContent ?? '다음',
          BoxStyle: this.normalizeStyle(box.BoxStyle || box.style),
          titleStyle: this.normalizeStyle(box.titleStyle),
          contentStyle: this.normalizeStyle(box.contentStyle),
          btnStyle: this.normalizeStyle(box.btnStyle)
        }));
        break;
      }

      case 'REMOVE_NEXT_BTN_BOX':
        this.engine.runCommand(new RemoveNextBtnBoxCommand(payload.regionId, payload.layerId, payload.boxId));
        break;

      // 7. 데이터 및 시스템 제어
      case 'ADD_DATA':
        // [중요] 만약 payload.data가 빈 배열([])로 들어온다면 초기화 의도로 파악합니다.
        // 현재 엔진은 addData 시 기존 데이터에 누적하므로, 
        // 완전한 초기화를 위해선 엔진의 addData 내부 로직 수정이 필요할 수 있습니다.
        if (payload.data) {
          this.engine.addData(payload.data);
        }
        break;

      case 'UNDO':
        this.engine.undo();
        break;

      default:
        console.warn(`[FlowRunner] 알 수 없는 액션 타입입니다: ${type}`);
    }
  }
}